import React, { useContext, type FC, type PropsWithChildren } from 'react';

import { theme, variant, getColor } from '@lucid/theme';
import styled from '@emotion/styled';
import kebabCase from 'lodash/kebabCase';
import { withImage, useSmartlingStrings } from '@lucid/hooks';
import { isDefined } from '@lucid/utils';

import reactHTMLParser from '../../utils/reactHTMLParser';
import type { Lucid } from '../../types/types';
import Box from '../box/box';
import Heading, { type HeadingTagsType } from '../heading/heading';
import Image from '../image/image';
import Link, { type LinkProps } from '../link/link';
import Text from '../text/text';
import ResourceCard from '../cards/resource-card/resource-card';
import LayoutContext from '../context/layout-context';
import ImageWithPlaceholder from '../image/image-with-placeholder';

interface FeatureGridLinkProps extends Omit<LinkProps, 'to'> {
  variant?: Lucid.Theme.Component.Link.VariantKeys | Lucid.Theme.Component.Button.VariantKeys;
}

type AlternatingFeatureGridProps = {
  variant?: 'alternating' | 'alternatingReversed';
};

type NonAlternatingFeatureGridProps = {
  variant?: Exclude<Lucid.Theme.Component.FeatureGrid.Container.VariantKeys, AlternatingFeatureGridProps['variant']>;
};

export type FeatureGridProps = {
  /** Allows the component to get additional styles. */
  className?: string;
  /** if the background for this section is dark */
  hasDarkBackground?: boolean;
  /** Set Aspect Ratio for the images */
  imageRatio?: Lucid.Generic.AspectRatio;
  /** Items to be rendered inside the feature grid */
  items: Lucid.Directus.Generated.Feature_Grid_Item[] | Lucid.Directus.Generated.Link_Hub_Item[];
  /** Variant for item title */
  itemTitleVariant?: Lucid.Theme.Component.Typography.VariantKeys;
  /** Modified props for the Link **/
  linkProps?: FeatureGridLinkProps;
  /** Optional Image above grid */
  sectionImage?: Lucid.Directus.Generated.Directus_Files;
  /** Optional Title above grid */
  sectionTitle?: string;
  /** Optional Subtitle above grid */
  sectionSubtitle?: string;
  /** Subtitle Text Variant -  */
  subtitleTextVariant?: Lucid.Theme.Component.Typography.VariantKeys;
  /** Optional CTA beneath grid */
  sectionCta?: Lucid.Directus.Generated.Links;
  /** Sets the text variant for the Section Title */
  titleTextVariant?: Lucid.Theme.Component.Typography.VariantKeys;
  /** The color of the section heading */
  titleColor?: Lucid.Theme.Color.ValueKeys | Lucid.Theme.Utility.ProductColorMap;
  /** Section Heading Variant(s) -  */
  headingVariant?: Lucid.Theme.Component.FeatureGrid.Heading.VariantKeys;
  /** The tag to use for the section title */
  headingAs?: HeadingTagsType;
  /** Amount of columns to render on screens tablet-sized and above */
  columns?: 1 | 2 | 3 | 4;
  /** Feature Grid Layout Variant */
  variant?: Lucid.Theme.Component.FeatureGrid.Container.VariantKeys;
} & (AlternatingFeatureGridProps | NonAlternatingFeatureGridProps);

const FeatureGridHeadingSection = styled(Box)(
  variant({
    scale: 'featureGrid.heading',
  }),
  {
    display: 'flex',
    flexDirection: 'column',
  },
);

const FeatureGridContainer = styled(Box)(
  variant({
    scale: 'featureGrid.container',
  }),
  ({ columns, variant }) => [
    {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
      gap: theme.space.s40,
    },
    columns && [
      {
        columnGap: columns === 2 ? theme.space.s104 : theme.space.s64,
        [theme.breakpoints.bp500.min]: {
          gridTemplateColumns: 'repeat(2, 1fr)',
        },
        [theme.breakpoints.bp900.min]: {
          gridTemplateColumns: `repeat(${columns}, 1fr)`,
        },
      },
    ],
    ['default'].includes(variant as string) && [
      {
        rowGap: theme.space.s80,
        columnCount: theme.space.s40,
      },
      columns === 2 && {
        gap: theme.space.s64,
        [theme.breakpoints.bp500.min]: {
          gridTemplateColumns: '1fr',
        },
        [theme.breakpoints.bp768.min]: {
          gridTemplateColumns: 'repeat(2, 1fr)',
        },
      },
    ],
    ['alternating', 'alternatingReversed'].includes(variant as string) && {
      display: 'flex',
    },
    ['resources'].includes(variant as string) && {
      textAlign: 'left',
      justifyContent: 'center',
      display: 'flex',
      [theme.breakpoints.bp768.min]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
      },
      [theme.breakpoints.bp1024.min]: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
      },
    },
  ],
);

const isFeatureGridItem = (
  item: Lucid.Directus.Generated.Link_Hub_Item | Lucid.Directus.Generated.Feature_Grid_Item,
): item is Lucid.Directus.Generated.Feature_Grid_Item => 'html' in item || 'link' in item || 'image' in item;

const normalizeItems = (
  items: Lucid.Directus.Generated.Feature_Grid_Item[] | Lucid.Directus.Generated.Link_Hub_Item[],
) =>
  items.map(item => {
    const isFeatureGrid = isFeatureGridItem(item);

    return {
      heading: item.heading,
      html: isFeatureGrid ? item.html : `<p>${item.text}</p>`,
      links: (isFeatureGrid
        ? item.link && [item.link]
        : item.link_set && item.link_set.map(linkItem => linkItem.links_id)
      )?.filter(isDefined),
      image: isFeatureGrid ? item.image : undefined,
    };
  });

const FeatureGrid: FC<PropsWithChildren<FeatureGridProps>> = ({
  className,
  hasDarkBackground,
  imageRatio,
  items,
  itemTitleVariant = 'headingM',
  linkProps,
  sectionSubtitle,
  subtitleTextVariant,
  sectionImage,
  sectionTitle,
  sectionCta,
  titleColor,
  titleTextVariant,
  headingVariant = 'default',
  headingAs = 'h2',
  columns,
  variant = 'default',
  ...rest
}) => {
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: ['mktg_read_more_about'],
    shortCode,
  });
  const isSideBySide = variant === 'default' && columns === 2;

  return (
    <Box
      as="section"
      className={className}
      css={theme => [
        variant !== 'resources' && {
          [theme.breakpoints.bp768.min]: { padding: isSideBySide ? 0 : theme.space.s80 },
        },
      ]}
    >
      <FeatureGridHeadingSection variant={variant === 'resources' ? 'leftAlign' : headingVariant}>
        {sectionTitle && (
          <Heading
            as={headingAs}
            css={theme => ({
              color: getColor({ color: titleColor, theme }),
              maxWidth: '100%',
              marginBottom: sectionSubtitle ? theme.space.s40 : theme.space.s80,
              [theme.breakpoints.bp900.min]: {
                maxWidth: '780px',
              },
            })}
            // eslint-disable-next-line @stylistic/jsx-sort-props
            className="featureGridSectionTitle"
            variant={titleTextVariant || 'heading2XL'}
          >
            {sectionTitle}
          </Heading>
        )}
        {sectionSubtitle && (
          <Text
            css={theme => ({
              maxWidth: '100%',
              color: hasDarkBackground ? theme.colors.white : theme.colors.coolGrey[100],
              marginBottom: sectionImage
                ? `${theme.space.s16}px`
                : isSideBySide
                  ? `${theme.space.s80}px`
                  : `${theme.space.s64}px`,
              [theme.breakpoints.bp900.min]: {
                maxWidth: '780px',
              },
            })}
            variant={subtitleTextVariant || 'paragraphL400'}
          >
            {sectionSubtitle}
          </Text>
        )}
        {sectionImage && (
          <Box
            css={theme => ({
              label: 'image',
              marginBottom: theme.space.s40,
              [theme.breakpoints.bp768.min]: {
                paddingTop: theme.space.s40,
                width: '100%',
              },
              [theme.breakpoints.bp900.min]: {
                marginBottom: theme.space.s104,
              },
            })}
          >
            <Image
              css={{
                height: '100%',
                width: '100%',
                objectFit: 'cover',
              }}
              dataElementPageLocation="body"
              imageAlt=""
              imageSrc={withImage(sectionImage) || ''}
              loading="lazy"
            />
          </Box>
        )}
      </FeatureGridHeadingSection>
      <Box
        css={() => [
          (variant === 'alternating' || variant === 'alternatingReversed') && {
            display: 'flex',
            justifyContent: 'center',
          },
        ]}
      >
        <FeatureGridContainer {...rest} columns={columns} variant={variant}>
          {normalizeItems(items).map(({ heading, html, image, links }, index) =>
            variant === 'cards' ? (
              <ResourceCard
                key={`${kebabCase(heading)}-${index + 1}`}
                heading={heading}
                link={{
                  final_url: links?.[0]?.final_url,
                  default_link_text: links?.[0]?.default_link_text,
                  accessibleDescription: withSmartling('mktg_read_more_about', {
                    title: heading || '',
                  }),
                }}
              >
                {html && (
                  <Text css={{ margin: 0 }} id={kebabCase([heading || '', `${index + 1}`])} variant="paragraphM400">
                    {reactHTMLParser(html, { replaceOptions: { replaceTag: 'span' } })}
                  </Text>
                )}
              </ResourceCard>
            ) : variant === 'resources' ? (
              <Box
                key={`${kebabCase(heading)}-${index + 1}`}
                css={theme =>
                  normalizeItems(items).length !== index + 1 && {
                    paddingRight: theme.space.s40,
                    height: '100%',
                    [theme.breakpoints.bp768.min]: {
                      '&:nth-of-type(odd)': {
                        borderRight: `1px solid ${getColor({ color: titleColor, theme }) || theme.colors.coolGrey[100]}`,
                      },
                    },
                    [theme.breakpoints.bp1024.min]: {
                      borderRight: `1px solid ${getColor({ color: titleColor, theme }) || theme.colors.coolGrey[100]}`,
                    },
                  }
                }
                // eslint-disable-next-line @stylistic/jsx-sort-props
                className="featureGridItem"
              >
                <Box>
                  <Heading
                    as="h3"
                    css={theme => ({ color: getColor({ color: titleColor, theme }) })}
                    variant={itemTitleVariant || 'headingL700'}
                  >
                    {heading}
                  </Heading>
                  {html && (
                    <Text
                      css={theme => ({ color: hasDarkBackground ? theme.colors.white : theme.colors.coolGrey[100] })}
                      id={kebabCase([heading || '', `${index + 1}`])}
                      variant="paragraphM400"
                    >
                      {reactHTMLParser(html, { replaceOptions: { replaceTag: 'span' } })}
                    </Text>
                  )}
                  {links && !!links.length && (
                    <Box css={{ display: 'flex', flexDirection: 'column' }}>
                      {links &&
                        links.map(link => (
                          <Link
                            key={link.final_url}
                            aria-label={`${heading} - ${link.default_link_text}`}
                            {...linkProps}
                            aria-describedby={kebabCase([heading || '', `${index + 1}`])}
                            href={link.final_url}
                            iconAnimation="navigational"
                            variant={hasDarkBackground ? 'primaryInverse' : 'primaryAlternate'}
                          >
                            {link.default_link_text}
                          </Link>
                        ))}
                    </Box>
                  )}
                </Box>
              </Box>
            ) : (
              <Box
                key={`${kebabCase(heading)}-${index + 1}`}
                css={{ alignItems: 'center', justifyContent: 'center' }}
                // eslint-disable-next-line @stylistic/jsx-sort-props
                className="featureGridItem"
              >
                {image && image.filename_disk && (
                  <>
                    {imageRatio ? (
                      <ImageWithPlaceholder
                        alt={image.translations?.[0]?.alt || ''}
                        aspectRatio={imageRatio}
                        css={theme => ({
                          marginBottom: isSideBySide ? theme.space.s32 : theme.space.s24,
                          maxWidth: image?.tags?.includes('small') ? '200px' : '100%',
                        })}
                        loading="lazy"
                        src={withImage(image)}
                      />
                    ) : (
                      <Image
                        css={theme => ({
                          marginBottom: isSideBySide ? theme.space.s32 : theme.space.s24,
                          maxWidth: image?.tags?.includes('small') ? '200px' : '100%',
                        })}
                        imageAlt={image.translations?.[0]?.alt || ''}
                        imageSrc={withImage(image)}
                        loading="lazy"
                      />
                    )}
                  </>
                )}
                <Box className="textContainer">
                  <Heading
                    as="h3"
                    css={theme => ({
                      color: hasDarkBackground ? theme.colors.white : theme.colors.coolGrey[100],
                      marginBottom: isSideBySide ? theme.space.s16 : theme.space.s24,
                    })}
                    variant={itemTitleVariant}
                  >
                    {heading}
                  </Heading>
                  {html && (
                    <Text
                      css={theme => ({
                        color: hasDarkBackground ? theme.colors.white : theme.colors.coolGrey[100],
                        marginBottom: isSideBySide ? theme.space.s16 : theme.space.s24,
                      })}
                      id={kebabCase([heading || '', `${index + 1}`])}
                      variant="paragraphS400"
                    >
                      {reactHTMLParser(html, {
                        replaceOptions: {
                          replaceTag: 'span',
                        },
                      })}
                    </Text>
                  )}
                  {links && !!links.length && (
                    <Box css={{ display: 'flex', flexDirection: 'column' }}>
                      {links &&
                        links.map(link => (
                          <Link
                            key={link.final_url}
                            {...linkProps}
                            aria-describedby={kebabCase([heading || '', `${index + 1}`])}
                            aria-label={`${link.default_link_text} - ${heading}`}
                            href={link.final_url}
                            iconAnimation="navigational"
                            variant={hasDarkBackground ? 'primaryInverse' : 'primary'}
                          >
                            {link.default_link_text}
                          </Link>
                        ))}
                    </Box>
                  )}
                </Box>
              </Box>
            ),
          )}
        </FeatureGridContainer>
      </Box>
      {sectionCta?.default_link_text && (
        <Box
          css={theme => ({
            marginTop: theme.space.s104,
            fontSize: theme.fontSizes.fs20,
            [theme.breakpoints.bp768.min]: {
              textAlign: 'center',
            },
          })}
        >
          <Link href={sectionCta.final_url} iconAnimation="navigational">
            {sectionCta.default_link_text}
          </Link>
        </Box>
      )}
    </Box>
  );
};

export default FeatureGrid;

export { FeatureGrid };
